'use client'

import type { Space } from '@cocoonspace/sdk-js/types/space'
import { useEffect, useState } from 'react'
import { Button } from '@cocoonspace/ui/components/button'
import {
	Carousel,
	CarouselItem,
	CarouselContent,
	CarouselPrevious,
	CarouselNext,
	CarouselApi,
} from '@cocoonspace/ui/components/carousel'
import {
	Dialog,
	DialogContent,
	DialogTrigger,
} from '@cocoonspace/ui/components/dialog'
import { useLingui } from '@lingui/react/macro'
import { TwicImg } from '@twicpics/components/react'
import { ArrowLeftIcon, MapPinIcon, XIcon } from 'lucide-react'

import { useSpaceAddress } from '../../hooks/use-space-address.hook'
import { SpaceCapacity } from '../atoms/space-capacity'
import { SpaceSurface } from '../atoms/space-surface'

const SpaceInfos = ({
	space,
	onClose,
}: {
	space: Space
	onClose: () => void
}) => {
	const { fullAddress } = useSpaceAddress(space, true)

	return (
		<div className='absolute top-0 left-0 w-[427px] rounded-br border-r border-b bg-white/70 p-4'>
			<h1 className='text-2xl font-bold'>{space.title}</h1>

			<div className='flex flex-row items-center truncate text-sm'>
				<MapPinIcon
					className='mr-1 size-4 shrink-0'
					strokeWidth={1.5}
				/>

				{fullAddress}
			</div>

			<div className='flex flex-row items-center justify-between'>
				<div className='flex gap-2 text-sm'>
					<SpaceCapacity space={space} />
					<SpaceSurface space={space} />
				</div>

				<Button
					variant='textPrimary'
					size='sm'
					className='focus-visible:ring-0'
					onClick={onClose}
				>
					<ArrowLeftIcon />
					Retourner à l'espace
				</Button>
			</div>
		</div>
	)
}

export const SpacePhotosDialogContent = ({
	space,
	activeIndex = 0,
	onClose,
}: {
	space: Space
	activeIndex?: number
	onClose: () => void
}) => {
	const [api, setApi] = useState<CarouselApi>()
	const [_index, setIndex] = useState(activeIndex || 0)
	const { t } = useLingui()

	const [options] = useState({
		loop: true,
		slidesToScroll: 1,
		startIndex: activeIndex ?? 0,
	})

	useEffect(() => {
		if (!api) return

		api.on('select', () => {
			setIndex(api.selectedScrollSnap())
		})
	}, [api])

	return (
		<>
			<Carousel
				className='h-full w-full'
				opts={options}
				setApi={setApi}
			>
				<CarouselContent>
					{space.images?.map((image, index) => (
						<CarouselItem key={index}>
							{['floor_plan', 'plan'].includes(image.type) ? (
								<TwicImg
									src={image.path}
									alt={space?.title}
									aria-label={space?.title}
									preTransform='quality=80/inside=1024x768'
									ratio='16/9'
									mode='contain'
									className='m-4 mx-auto'
								/>
							) : (
								<TwicImg
									src={image.path}
									alt={space?.title}
									aria-label={space?.title}
									preTransform='quality=80/inside=1024x768'
									ratio='3/2'
									focus='50px100p'
									mode='cover'
								/>
							)}
						</CarouselItem>
					))}
				</CarouselContent>

				<CarouselPrevious className='!left-8 z-50 bg-white/70' />
				<CarouselNext className='!right-8 z-50 bg-white/70' />
			</Carousel>

			<SpaceInfos
				space={space}
				onClose={onClose}
			/>

			<Button
				variant='ghost'
				onClick={onClose}
				icon
				className='absolute top-8 right-8 z-50 bg-white/70'
				aria-label={t`Fermer`}
			>
				<XIcon />
			</Button>
		</>
	)
}

export const SpacePhotosDialog = ({
	children,
	space,
	activeIndex = 0,
}: {
	children: React.ReactNode
	space: Space
	activeIndex?: number
}) => {
	const [open, setOpen] = useState(false)

	return (
		<Dialog
			open={open}
			onOpenChange={setOpen}
		>
			<DialogTrigger asChild>{children}</DialogTrigger>

			<DialogContent
				className='h-screen w-screen !max-w-full !rounded-none p-0'
				hideCloseBtn
			>
				<SpacePhotosDialogContent
					space={space}
					activeIndex={activeIndex}
					onClose={() => setOpen(false)}
				/>
			</DialogContent>
		</Dialog>
	)
}
